import { useStrictURLDaterange } from "../lib/urlParams";
import { useState } from "react";
import { isThisYear } from "../lib/dateUtils";
import { Button, Modal, Nav } from "react-bootstrap";
import styles from "./TimeRange.module.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { DateTime } from "luxon";
import DatePicker from "react-datepicker";

function FormattedDate({ value }: { value: Date }) {
    const dateTime = DateTime.fromJSDate(value);
    const dateFormat = isThisYear(dateTime) ? "EEE d MMM" : "EEE d MMM yyyy";
    return (
        <>
            <span className={styles.date}>{dateTime.toFormat(dateFormat)}</span>
        </>
    );
}

export function DateRangeDisplay() {
    const [urlDateRange] = useStrictURLDaterange();
    const [showDateRangeSelector, setShowDateRangeSelector] = useState(false);

    function handleClick() {
        setShowDateRangeSelector(true);
    }

    function hideSelector() {
        setShowDateRangeSelector(false);
    }

    return (
        <>
            <Button
                onClick={handleClick}
                className={styles.openSelectorButton}
                variant={"tribe-primary"}
                onFocus={
                    /* Otherwise the button is left focused after you cancel */
                    (e) => e.target.blur()
                }
            >
                <FormattedDate value={urlDateRange.start} />
                <FontAwesomeIcon
                    icon={faArrowRight}
                    className={styles.fromToArrow}
                />
                <FormattedDate value={urlDateRange.end} />
            </Button>

            {showDateRangeSelector && (
                <DateRangeSelector onClose={hideSelector} />
            )}
        </>
    );
}

export function DateRangeSelector(props: { onClose: () => void }) {
    const [selectorType, setSelectorType] = useState("custom");

    function close() {
        props.onClose();
    }

    return (
        <Modal
            show={true}
            onHide={close}
            dialogClassName={"modal-lg " + styles.timeRangeSelector}
        >
            <Modal.Header>
                <Nav variant={"pills"}>
                    <Nav.Item>
                        <Button
                            className={
                                selectorType === "custom" ? "active" : ""
                            }
                            onClick={() => {
                                setSelectorType("custom");
                            }}
                            variant={"tribe-primary"}
                        >
                            Custom
                        </Button>
                    </Nav.Item>
                </Nav>
            </Modal.Header>
            {selectorType === "custom" && (
                <CustomDateRangeSelector onClose={close} />
            )}
        </Modal>
    );
}

export function CustomDateRangeSelector(props: { onClose: () => void }) {
    const [urlDateRange, setUrlDaterange] = useStrictURLDaterange();
    const [startDate, setStartDate] = useState(urlDateRange.start);
    const [endDate, setEndDate] = useState(urlDateRange.end);

    function handleSave() {
        setUrlDaterange({
            start: startDate,
            end: endDate,
        });
        props.onClose();
    }

    function handleSetStartDate(date: Date) {
        setStartDate(date);
    }

    function handleSetEndDate(date: Date) {
        setEndDate(date);
    }

    return (
        <>
            <Modal.Body className={styles.customTimeRangeSelector}>
                <div className={"d-flex flex-column"}>
                    <div className={styles.dateSection}>
                        From: <FormattedDate value={startDate} />
                    </div>
                    <DatePicker
                        selected={startDate}
                        inline
                        onChange={handleSetStartDate}
                        maxDate={endDate}
                        chooseDayAriaLabelPrefix="Choose to start"
                    />
                </div>
                <div className={"d-flex flex-column"}>
                    <div className={styles.dateSection}>
                        To: <FormattedDate value={endDate} />
                    </div>
                    <DatePicker
                        selected={endDate}
                        inline
                        onChange={handleSetEndDate}
                        minDate={startDate}
                        chooseDayAriaLabelPrefix="Choose to end"
                    />
                </div>
            </Modal.Body>

            <Modal.Footer>
                <Button
                    variant="outline-tribe-secondary"
                    onClick={props.onClose}
                >
                    Cancel
                </Button>
                <Button variant="outline-tribe-primary" onClick={handleSave}>
                    Save
                </Button>
            </Modal.Footer>
        </>
    );
}
